import React, { useState, useRef, useEffect } from 'react';
import { useUiStore } from '@/store/uiStore';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { Icon } from '@/ui/icons/Icon';
import { HMIComponentRenderer } from './HMIComponentRenderer';
import styles from './HMIRuntimeWindow.module.css';

const PREVIEW_WIDTH = 420;

export function HMIRuntimeWindow() {
  const hmiOverlayOpen = useUiStore((s) => s.hmiOverlayOpen);
  const setHmiOverlayOpen = useUiStore((s) => s.setHmiOverlayOpen);
  const hmiOverlayMinimized = useUiStore((s) => s.hmiOverlayMinimized);
  const setHmiOverlayMinimized = useUiStore((s) => s.setHmiOverlayMinimized);

  const hmi = useProjectStore((s) => s.project.hmi);
  const simState = useSimulationStore((s) => s.snapshot.state);
  const isRunning = simState === 'RUNNING';

  const [activeScreenId, setActiveScreenId] = useState<string | null>(null);

  // Position state (draggable window)
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Initialize position to top-right on mount
  useEffect(() => {
    const defaultX = Math.max(20, (window.innerWidth || 1200) - PREVIEW_WIDTH - 320);
    setPos({ x: defaultX, y: 70 });
  }, []);

  // Sync active screen if screens change
  useEffect(() => {
    if (hmi?.screens && hmi.screens.length > 0) {
      const exists = hmi.screens.some((s) => s.id === activeScreenId);
      if (!activeScreenId || !exists) {
        setActiveScreenId(hmi.defaultScreenId || hmi.screens[0]?.id || null);
      }
    }
  }, [hmi, activeScreenId]);

  if (!hmiOverlayOpen) return null;

  const currentScreen = hmi.screens.find((s) => s.id === activeScreenId) || hmi.screens[0];

  // Dragging handlers
  const handleHeaderPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).tagName === 'SELECT' || (e.target as HTMLElement).tagName === 'BUTTON') {
      return;
    }
    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: pos.x,
      posY: pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleHeaderPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const newX = Math.max(10, Math.min(window.innerWidth - 100, dragStartRef.current.posX + dx));
    const newY = Math.max(40, Math.min(window.innerHeight - 80, dragStartRef.current.posY + dy));
    setPos({ x: newX, y: newY });
  };

  const handleHeaderPointerUp = (e: React.PointerEvent) => {
    isDraggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  // Minimized dock pill
  if (hmiOverlayMinimized) {
    return (
      <div
        className={styles.minimizedPill}
        style={{ left: pos.x, top: pos.y }}
        onClick={() => setHmiOverlayMinimized(false)}
        title="HMI Operatör Panelini Büyüt"
      >
        <span
          className={styles.liveDot}
          style={{ backgroundColor: isRunning ? '#10b981' : '#9ca3af' }}
        />
        <Icon name="app" size={16} />
        <span>HMI: {currentScreen?.name || 'Operatör Paneli'}</span>
        <button
          className={styles.headerBtn}
          onClick={(e) => {
            e.stopPropagation();
            setHmiOverlayOpen(false);
          }}
          title="Kapat"
        >
          ✕
        </button>
      </div>
    );
  }

  const screenW = currentScreen?.width || 800;
  const screenH = currentScreen?.height || 600;
  const scale = PREVIEW_WIDTH / screenW;
  const previewHeight = Math.round(screenH * scale);

  return (
    <div
      className={styles.windowContainer}
      style={{ left: pos.x, top: pos.y, width: PREVIEW_WIDTH + 24 }}
    >
      {/* Draggable Header */}
      <div
        className={styles.header}
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
      >
        <div className={styles.headerLeft}>
          <Icon name="app" size={16} />
          <span className={styles.title}>HMI Panel</span>
          <span
            className={`${styles.liveBadge} ${isRunning ? styles.liveBadgeRunning : styles.liveBadgeStopped}`}
          >
            <span className={styles.liveDot} />
            {isRunning ? 'CANLI' : 'HAZIR'}
          </span>
        </div>

        <div className={styles.headerRight}>
          {hmi.screens.length > 1 && (
            <select
              className={styles.screenSelect}
              value={currentScreen?.id || ''}
              onChange={(e) => setActiveScreenId(e.target.value)}
            >
              {hmi.screens.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            className={styles.headerBtn}
            onClick={() => setHmiOverlayMinimized(true)}
            title="Küçült"
            aria-label="Küçült"
          >
            –
          </button>
          <button
            type="button"
            className={`${styles.headerBtn} ${styles.headerBtnClose}`}
            onClick={() => setHmiOverlayOpen(false)}
            title="Kapat"
            aria-label="Kapat"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Industrial Screen Bezel */}
      <div className={styles.bezel}>
        {!currentScreen || currentScreen.components.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Bu ekranda henüz HMI bileşeni yok.</p>
            <p style={{ marginTop: 4, color: '#6b7280' }}>
              Üst menüden <strong>HMI</strong> sekmesine geçip buton, lamba ve göstergeler ekleyebilirsiniz.
            </p>
          </div>
        ) : (
          <div
            className={styles.viewportContainer}
            style={{
              width: PREVIEW_WIDTH,
              height: previewHeight,
              backgroundColor: currentScreen.backgroundColor || '#1e1e1e',
            }}
          >
            <div
              className={styles.canvasInner}
              style={{
                width: screenW,
                height: screenH,
                transform: `scale(${scale})`,
              }}
            >
              {currentScreen.components.map((comp) => (
                <div
                  key={comp.id}
                  style={{
                    position: 'absolute',
                    left: comp.x,
                    top: comp.y,
                    width: comp.width,
                    height: comp.height,
                  }}
                >
                  <HMIComponentRenderer component={comp} isRuntime={true} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
